import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged, User, connectAuthEmulator } from 'firebase/auth';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { getFirestore, collection, getDocs, query, orderBy, limit, doc, onSnapshot, connectFirestoreEmulator, where } from 'firebase/firestore';
import { getNonDeployedFirebaseConfig } from './firebase.config';
import {
  TestExecutionDocument,
  TestExecutionSubjectResult,
  TestExecutionSubjectResultDocument,
  RolloutSetupOptions,
  SetupTaskOptions,
  UserGroup,
  ProgramGroup,
  AddEnvironmentRequest,
  UpdateEnvironmentRequest,
  SetEnvironmentStatusMessageRequest,
  UpdateUserProfileRequest,
  UserAutodeployConfig,
  CreatePoolRequest,
  UpdatePoolRequest,
} from "@finvari/deliver";

// Firebase configuration
let firebaseConfig: any = null;
let app: any = null;

// Initialize Firebase
const initializeFirebase = async () => {
  if (app) return app; // Already initialized

  try {
    if (!isLocalDevelopment()) {
      // Try to load config from Firebase hosting environment
      const response = await fetch('/__/firebase/init.json');
      if (response.ok) {
        firebaseConfig = await response.json();
        app = initializeApp(firebaseConfig);
        return app;
      }
    }
  } catch (error) {
    console.error('Error loading Firebase config:', error);
  }

  // Fallback to hardcoded config
  firebaseConfig = getNonDeployedFirebaseConfig();
  app = initializeApp(firebaseConfig);
  return app;
}

// Initialize Firebase services
let auth: any = null;
let functions: any = null;
let firestore: any = null;
let googleProvider: any = null;
let emulatorsConnected = false;

const isLocalDevelopment = () => {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

// Initialize Firebase services
const initializeServices = async () => {
  if (auth && functions && firestore) return { auth, functions, firestore }; // Already initialized

  const firebaseApp = await initializeFirebase();
  auth = getAuth(firebaseApp);
  functions = getFunctions(firebaseApp);
  firestore = getFirestore(firebaseApp);

  if (isLocalDevelopment() && !emulatorsConnected) {
    try {
      if (process.env.REACT_APP_FIREBASE_FUNCTIONS_EMULATOR_HOST) {
        const [host, port] = process.env.REACT_APP_FIREBASE_FUNCTIONS_EMULATOR_HOST.split(':');
        connectFunctionsEmulator(functions, host, parseInt(port, 10));
        console.log('✓ Connected to Functions Emulator on port ' + port);
      }
      if (process.env.REACT_APP_FIRESTORE_EMULATOR_HOST) {
        const [host, port] = process.env.REACT_APP_FIRESTORE_EMULATOR_HOST.split(':');
        connectFirestoreEmulator(firestore, host, parseInt(port, 10));
        console.log('✓ Connected to Firestore Emulator on port ' + port);
      }
      if (process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST) {
        connectAuthEmulator(auth, 'http://' + process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST, { disableWarnings: true });
        console.log('✓ Connected to Auth Emulator on ' + process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST);
      }
      emulatorsConnected = true;
    } catch (error) {
      console.warn('⚠️ Failed to connect to some emulators:', error);
    }
  }

  // Configure auth for GCP Identity Platform
  auth.useDeviceLanguage();

  // Google Auth Provider
  googleProvider = new GoogleAuthProvider();
  googleProvider.setCustomParameters({
    prompt: 'select_account'
  });

  return { auth, functions, firestore };
};

// Authentication functions
export const signInWithGoogle = async (): Promise<User> => {
  const { auth } = await initializeServices();
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return result.user;
  } catch (error) {
    console.error('Error signing in with Google:', error);
    throw error;
  }
};

export const signOutUser = async (): Promise<void> => {
  const { auth } = await initializeServices();
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

// Auth state observer
export const onAuthStateChange = async (callback: (user: User | null) => void) => {
  const { auth } = await initializeServices();
  return onAuthStateChanged(auth, callback);
};

// Cloud Functions
export const callHealthCheck = async (...args: any[]) => {
  const { functions } = await initializeServices();
  const healthCheckFunction = httpsCallable(functions, 'healthCheck');
  return healthCheckFunction(...args);
};

// New Firestore-based functions
export const refreshGitHubPullRequests = async () => {
  const { functions } = await initializeServices();
  const functionCall = httpsCallable(functions, 'refreshGitHubPullRequests');
  return functionCall();
};

export const updateUserProfile = async (data: UpdateUserProfileRequest) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'updateUserProfile');
  return fn(data);
};

// Save (or clear, when passed null) the caller's personal autodeploy configuration.
export const setUserAutodeployConfig = async (config: UserAutodeployConfig | null) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'setUserAutodeployConfig');
  return fn(config);
};

export const sendTestNotification = async () => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'sendTestNotification');
  return fn({});
};

export const createDeploymentForPullRequest = async (
  prNumber: number,
  environment: string,
  modules?: string[],
  setupOptions?: RolloutSetupOptions,
  poolId?: string,
  autoDeployOnPush?: boolean
) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'createDeploymentForPullRequest');
  return fn({ prNumber, environment, modules, setupOptions, poolId, autoDeployOnPush });
};

export const retryDeployment = async (rolloutId: string, module: string) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'retryDeployment');
  return fn({ rolloutId, module });
};

export const createDeploymentForMain = async (
  environment: string,
  modules?: string[],
  setupOptions?: RolloutSetupOptions,
  poolId?: string,
  autoDeployOnPush?: boolean
) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'createDeploymentForMain');
  return fn({ environment, modules, setupOptions, poolId, autoDeployOnPush });
};

export const startTestExecution = async (testSuiteId: string, rolloutId: string, subjects?: string[], testSteps?: string[], baselineExecutionId?: string, comments?: string, acknowledgedDataMismatch?: boolean) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'startTestExecution');
  return fn({ testSuiteId, rolloutId, subjects, testSteps, baselineExecutionId, comments, acknowledgedDataMismatch });
};

export const updateTestExecutionComments = async (executionId: string, comments: string) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'updateTestExecutionComments');
  return fn({ executionId, comments });
};

export const markTestExecutionFailed = async (executionId: string, testSuiteId: string) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'markTestExecutionFailed');
  return fn({ executionId, testSuiteId });
};

// Accept a stale-but-passing test run for a suite without rerunning it (records a waiver stamped
// with the PR's current head SHA). See functions/src/testExecution/waiveTestRerun.ts.
export const waiveTestRerun = async (prNumber: number, testSuiteId: string) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'waiveTestRerun');
  return fn({ prNumber, testSuiteId });
};

// Record that a human inspected a finished run's results and accepted them (stamps the execution
// with verifiedBy/verifiedAt). See functions/src/testExecution/verifyTestExecution.ts.
export const verifyTestExecution = async (executionId: string, note?: string) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'verifyTestExecution');
  return fn({ executionId, note });
};

export const rerunTestExecutionSubject = async (executionId: string, subject: string) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'rerunTestExecutionSubject');
  return fn({ executionId, subject });
};

// Record a human's verdict on one pattern-verification review item (a diff group, attestation, or
// log template). Every item must be accepted before verifyTestExecution will succeed; a rejected
// item blocks verification. See functions/src/testExecution/acknowledgePattern.ts.
export const acknowledgePattern = async (
  executionId: string,
  signature: string,
  verdict: 'accepted' | 'rejected',
  note?: string,
) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'acknowledgePattern');
  return fn({ executionId, signature, verdict, note });
};

// Re-queue the pattern analysis for a finished run whose analysis failed or died before reporting.
// The analysis is claimed once on the run's terminal transition, so without this a crashed analyzer
// leaves the run permanently unverifiable. See functions/src/testExecution/reanalyzeTestExecution.ts.
export const reanalyzeTestExecution = async (executionId: string) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'reanalyzeTestExecution');
  return fn({ executionId });
};

// Pin a completed no-baseline run as the golden baseline for its suite. The run dialog defaults to
// this so successive recompute runs diff against the same reference. See
// functions/src/testExecution/setGoldenBaseline.ts.
export const setGoldenBaseline = async (testSuiteId: string, executionId: string) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'setGoldenBaseline');
  return fn({ testSuiteId, executionId });
};

export const introspectTestSuites = async (rolloutId: string) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'introspectTestSuites');
  return fn({ rolloutId });
};

export const retrySetupTask = async (rolloutId: string, taskId: string, newOptions?: SetupTaskOptions) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'retrySetupTask');
  return fn({ rolloutId, taskId, newOptions });
};

export const createSetupTaskForRollout = async (rolloutId: string, options: SetupTaskOptions) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'createSetupTaskForRollout');
  return fn({ rolloutId, options });
};

export const failSetupTask = async (rolloutId: string, taskId: string) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'failSetupTask');
  return fn({ rolloutId, taskId });
};

export interface UpdateConfigRequest {
  modules?: string[];
  protectedEnvironments?: string[];
  userGroups?: Record<string, UserGroup>;
  programGroups?: Record<string, ProgramGroup>;
}

export const updateConfig = async (data: UpdateConfigRequest) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'updateConfig');
  return fn(data);
};

export const addEnvironmentToConfig = async (data: AddEnvironmentRequest) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'addEnvironmentToConfig');
  return fn(data);
};

export const updateEnvironment = async (data: UpdateEnvironmentRequest) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'updateEnvironment');
  return fn(data);
};

export const setEnvironmentStatusMessage = async (data: SetEnvironmentStatusMessageRequest) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'setEnvironmentStatusMessage');
  return fn(data);
};

// --- Environment pools ---

export const createPool = async (data: CreatePoolRequest) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'createPool');
  return fn(data);
};

export const updatePool = async (data: UpdatePoolRequest) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'updatePool');
  return fn(data);
};

export const deletePool = async (poolId: string) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'deletePool');
  return fn({ poolId });
};

export const extendLease = async (gcpProjectId: string) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'extendLease');
  return fn({ gcpProjectId });
};

export const releaseLease = async (gcpProjectId: string) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'releaseLease');
  return fn({ gcpProjectId });
};

export const cancelQueuedRollout = async (rolloutId: string) => {
  const { functions } = await initializeServices();
  const fn = httpsCallable(functions, 'cancelQueuedRollout');
  return fn({ rolloutId });
};

export const createArtifactSession = async () => {
  const { auth } = await initializeServices();
  const idToken = await auth.currentUser?.getIdToken();

  // we have to use a different method of calling this one so it can set
  // the cookie we need for checking auth when we proxy the request back through
  const response = await fetch('/api/artifact-session', {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${idToken}` },
    credentials: 'include'
  });

  if (!response.ok) throw new Error('Failed to create session');
  return response.json();
};

export const getBaselineCandidates = async (testSuiteId: string, maxResults = 50) => {
  const { firestore } = await initializeServices();
  const q = query(
    collection(firestore, "testExecutions"),
    where('testSuiteId', '==', testSuiteId),
    where('status', '==', 'completed'),
    orderBy("createdAt", "desc"),
    limit(maxResults)
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(d => d.data() as TestExecutionDocument);
};

export const getMostRecentTestExecution = async (testSuiteId: string, environmentId: string) => {
  const { firestore } = await initializeServices();
  const q = query(
    collection(firestore, "testExecutions"),
    where('testSuiteId', '==', testSuiteId),
    where('environment', '==', environmentId),
    orderBy("createdAt", "desc"),
    limit(1)
  );
  const querySnapshot = await getDocs(q);
  const docSnap = querySnapshot.docs[0];
  return docSnap ? docSnap.data() as TestExecutionDocument : null;
}

// Subscribe to the most recent test execution for a test suite/environment
export const subscribeToMostRecentTestExecution = async (
  testSuiteId: string,
  environmentId: string,
  callback: (data: TestExecutionDocument | null) => void,
  rolloutId?: string,
) => {
  const { firestore } = await initializeServices();
  const constraints = [
    where('testSuiteId', '==', testSuiteId),
    where('environment', '==', environmentId),
    ...(rolloutId ? [where('rolloutId', '==', rolloutId)] : []),
    orderBy("createdAt", "desc"),
    limit(1),
  ];
  const q = query(
    collection(firestore, "testExecutions"),
    ...constraints,
  );
  return onSnapshot(q, (snapshot) => {
    const docSnap = snapshot.docs[0];
    if (docSnap) {
      callback(docSnap.data() as TestExecutionDocument);
    } else {
      callback(null);
    }
  }, (error) => {
    console.error('Error subscribing to most recent test execution:', error);
  });
}

// Subscribe to test execution document
export const subscribeToTestExecution = async (executionId: string, callback: (data: TestExecutionDocument | null) => void) => {
  const { firestore } = await initializeServices();
  const docRef = doc(firestore, 'testExecutions', executionId);
  return onSnapshot(docRef, (doc) => {
    if (doc.exists()) {
      callback(doc.data() as TestExecutionDocument);
    } else {
      callback(null);
    }
  }, (error) => {
    console.error('Error subscribing to test execution:', error);
  });
};

// Subscribe to test executions for a given PR number
// Uses only prNumber filter — no composite index needed since createdAt ordering
// is applied client-side.
export const subscribeToTestExecutionsForPR = async (
  prNumber: number,
  callback: (data: TestExecutionDocument[]) => void,
  onError?: (error: Error) => void
) => {
  const { firestore } = await initializeServices();
  const q = query(
    collection(firestore, "testExecutions"),
    where('prNumber', '==', prNumber),
  );
  return onSnapshot(q, (snapshot) => {
    const executions: TestExecutionDocument[] = [];
    snapshot.forEach((doc) => {
      executions.push(doc.data() as TestExecutionDocument);
    });
    // Sort client-side to avoid needing a composite index
    executions.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    callback(executions);
  }, (error) => {
    console.error('Error subscribing to test executions for PR:', error);
    if (onError) onError(error);
  });
};

// Subscribe to test execution subject results subcollection
export const subscribeToTestExecutionResults = async (executionId: string, callback: (data: TestExecutionSubjectResult[]) => void) => {
  const { firestore } = await initializeServices();
  const collectionRef = collection(firestore, 'testExecutions', executionId, 'testExecutionResults');
  return onSnapshot(collectionRef, (snapshot) => {
    const results: TestExecutionSubjectResultDocument[] = [];
    snapshot.forEach((doc) => {
      results.push({ id: doc.id, ...doc.data() } as unknown as TestExecutionSubjectResultDocument);
    });
    callback(results);
  }, (error) => {
    console.error('Error subscribing to test execution results:', error);
  });
};


// Get initialized app
export const getApp = async () => {
  return await initializeFirebase();
};

const firebaseApi = { initializeFirebase, getApp };
export default firebaseApi;